import React, { useCallback, useEffect, useRef } from "react";
import appStoreBadge from "../assets/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg";
import googlePlayBadge from "../assets/GetItOnGooglePlay_Badge_Web_color_English.png";
import {
  BUSINESS_IOS_APP_STORE_URL,
  BUSINESS_GOOGLE_PLAY_URL,
  CUSTOMER_IOS_APP_STORE_URL,
  CUSTOMER_GOOGLE_PLAY_URL
} from "../config/appStoreConfig";
import {
  trackAppDownloadClick,
  trackAppDownloadImpression,
} from "../utils/analytics";
import {
  CONSENT_CHANGED_EVENT,
  hasAnalyticsConsent,
} from "../utils/consent";

const APP_LINKS = {
  business: {
    name: "ZediPass Business",
    context: "For businesses",
    iosUrl: BUSINESS_IOS_APP_STORE_URL,
    androidUrl: BUSINESS_GOOGLE_PLAY_URL
  },
  customer: {
    name: "ZediPass",
    context: "For customers",
    iosUrl: CUSTOMER_IOS_APP_STORE_URL,
    androidUrl: CUSTOMER_GOOGLE_PLAY_URL
  }
};

/**
 * App Store and Google Play links for the business or customer experience.
 */
const AppDownloadBadges = ({
  audience = "business",
  cards = false,
  className = "",
  compact = false,
  placement = "unspecified",
  preferredPlatform = "other",
  showLabel = false,
  size = "md"
}) => {
  const app = APP_LINKS[audience] || APP_LINKS.business;
  const badgeH = size === "sm" ? "h-9" : "h-11";
  const hasPreferredStore = cards && ["ios", "android"].includes(preferredPlatform);
  const containerRef = useRef(null);
  const impressionTrackedRef = useRef(false);

  const analyticsContext = useCallback(
    () => ({
      funnel_name: "app_download_interest",
      app_audience: audience,
      app_name: app.name,
      placement,
    }),
    [app.name, audience, placement]
  );

  const recordImpression = useCallback(() => {
    if (impressionTrackedRef.current || !hasAnalyticsConsent()) return;
    impressionTrackedRef.current = true;
    trackAppDownloadImpression({
      ...analyticsContext(),
      funnel_step_name: "store_badges_viewed",
      funnel_step_number: 1,
      stores_available: "apple_app_store,google_play",
      badge_count: hasPreferredStore ? 1 : 2,
      visibility_threshold: 0.5,
    });
  }, [analyticsContext, hasPreferredStore]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof window === "undefined") return undefined;

    impressionTrackedRef.current = false;
    let isVisible = false;
    const handleConsentChange = () => {
      if (isVisible) recordImpression();
    };

    let observer;
    if (typeof IntersectionObserver === "undefined") {
      isVisible = true;
      recordImpression();
    } else {
      observer = new IntersectionObserver(
        (entries) => {
          isVisible = entries.some(
            (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5
          );
          if (isVisible) recordImpression();
        },
        { threshold: [0.5] }
      );
      observer.observe(node);
    }

    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChange);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChange);
    };
  }, [recordImpression]);

  const trackStoreClick = (storePlatform, destinationHost) => {
    trackAppDownloadClick({
      ...analyticsContext(),
      funnel_step_name: "store_badge_clicked",
      funnel_step_number: 2,
      store_platform: storePlatform,
      destination_host: destinationHost,
      destination_type: "native_store_listing",
    });
  };

  const stores = [
    {
      id: "ios",
      url: app.iosUrl,
      analyticsPlatform: "apple_app_store",
      destinationHost: "apps.apple.com",
      linkLabel: `Download ${app.name} on the App Store`,
      image: appStoreBadge,
      imageAlt: "Download on the App Store",
    },
    {
      id: "android",
      url: app.androidUrl,
      analyticsPlatform: "google_play",
      destinationHost: "play.google.com",
      linkLabel: `Get ${app.name} on Google Play`,
      image: googlePlayBadge,
      imageAlt: "Get it on Google Play",
    },
  ];

  const renderStoreBadge = (store) => (
    <a
      key={store.id}
      href={store.url}
      target={cards ? undefined : "_blank"}
      rel={cards ? undefined : "noopener noreferrer"}
      onClick={() => trackStoreClick(store.analyticsPlatform, store.destinationHost)}
      aria-label={store.linkLabel}
      className={
        cards
          ? "group flex h-[clamp(6.4rem,32vw,7.9rem)] min-h-11 w-full touch-manipulation items-center justify-center rounded-2xl transition duration-200 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 motion-reduce:transform-none"
          : `inline-flex touch-manipulation rounded-lg transition duration-200 hover:opacity-90 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950`
      }
    >
      <img
        src={store.image}
        alt={store.imageAlt}
        className={
          cards
            ? "h-[76%] w-auto max-w-full object-contain drop-shadow-[0_18px_30px_rgba(56,189,248,0.18)]"
            : `${badgeH} w-auto`
        }
      />
    </a>
  );

  const primaryStore = stores.find(({ id }) => id === preferredPlatform);
  const alternateStore = stores.find(({ id }) => id !== preferredPlatform);

  return (
    <div
      ref={containerRef}
      className={`flex flex-col gap-2.5 ${className}`}
      aria-label={`Get ${app.name}`}
      data-app-download-placement={placement}
    >
      {showLabel ? (
        <p className="m-0 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
          {app.context} · {app.name}
        </p>
      ) : null}

      {hasPreferredStore ? (
        <div className="flex w-full flex-col items-center">
          {renderStoreBadge(primaryStore)}
          <details className="group/store mt-1 w-full text-center">
            <summary className="mx-auto inline-flex min-h-11 cursor-pointer touch-manipulation list-none items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold text-cyan-100 transition hover:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-cyan-300 [&::-webkit-details-marker]:hidden">
              Choose another store
              <span className="text-cyan-300 transition-transform group-open/store:rotate-180" aria-hidden="true">↓</span>
            </summary>
            <div className="mt-1">{renderStoreBadge(alternateStore)}</div>
          </details>
        </div>
      ) : (
        <div
          className={
            cards
              ? "flex w-full flex-col items-center gap-1"
              : compact
              ? "flex items-center justify-center gap-2.5"
              : "flex flex-wrap items-center gap-3"
          }
        >
          {stores.map(renderStoreBadge)}
        </div>
      )}
    </div>
  );
};

export default AppDownloadBadges;
